import axios from 'axios';

// Production-ready backend configuration for SSL environments
const getBackendUrl = () => {
  // Check if we're running locally or on production
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isLocalhost) {
    // Local development - use localhost backend
    return 'http://localhost:8001';
  } else {
    // Production - use gradi.ai
    return 'https://gradi.ai';
  }
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Configure Axios for HTTPS environments
axios.defaults.timeout = 300000; // 5 minutes for video analysis
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    // Ensure proper headers for HTTPS
    config.headers = {
      ...config.headers,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    
    // Add metadata for performance tracking
    config.metadata = { startTime: new Date().getTime() };
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with performance tracking
axios.interceptors.response.use(
  (response) => {
    // Calculate request duration for performance monitoring
    if (response.config.metadata) {
      const duration = new Date().getTime() - response.config.metadata.startTime;
      if (duration > 30000) { // Log slow requests
        console.warn(`Slow request detected: ${response.config.url} took ${duration}ms`);
      }
    }
    return response;
  },
  (error) => {
    // Only log actual errors, not successful responses
    if (error.response?.status >= 400) {
      console.error('API Error:', {
        status: error.response.status,
        url: error.config?.url,
        message: error.response?.data?.error || error.message,
        duration: error.config?.metadata ? 
          new Date().getTime() - error.config.metadata.startTime : 'unknown'
      });
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Enhanced health check
  async healthCheck() {
    try {
      const response = await axios.get(`${API}/health`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error.message);
      throw new Error('Cannot connect to backend server');
    }
  },

  // Get a presigned download URL for the Prompt Builder Chrome extension zip
  async getPromptBuilderDownloadUrl() {
    const response = await axios.get(`${API}/prompt-builder/download-url`);
    return response.data.url;
  },

  // Get total number of prompts generated by the Prompt Builder extension
  async getPromptBuilderUsage() {
    const response = await axios.get(`${API}/prompt-builder/usage`);
    return response.data.total_generated;
  },

  // Comprehensive video analysis with enhanced error handling
  async analyzeVideo(youtubeUrl, options = {}) {
    try {
      // Input validation
      if (!youtubeUrl || typeof youtubeUrl !== 'string') {
        throw new Error('Please provide a valid YouTube URL');
      }

      const cleanUrl = youtubeUrl.trim();

      // Comprehensive YouTube URL validation
      const youtubePatterns = [
        /^https?:\/\/(www\.)?(youtube\.com|youtu\.be|m\.youtube\.com)\/.+/,
        /^https?:\/\/(www\.)?youtube\.com\/watch\?.*v=[a-zA-Z0-9_-]{11}/,
        /^https?:\/\/youtu\.be\/[a-zA-Z0-9_-]{11}/,
        /^https?:\/\/(www\.)?youtube\.com\/embed\/[a-zA-Z0-9_-]{11}/,
        /^https?:\/\/(www\.)?youtube\.com\/shorts\/[a-zA-Z0-9_-]{11}/,
      ];

      const isValidUrl = youtubePatterns.some(pattern => pattern.test(cleanUrl));
      if (!isValidUrl) {
        throw new Error('Please enter a valid YouTube URL (youtube.com or youtu.be)');
      }

      // Request configuration
      const config = {
        headers: { 'Content-Type': 'application/json' },
        timeout: options.timeout || 300000, // 5 minutes default
        signal: options.signal,
      };

      const response = await axios.post(
        `${API}/analyze-video`, 
        { 
          youtube_url: cleanUrl,
          analysis_language: options.analysisLanguage || 'hinglish',
          category: options.category || null
        }, 
        config
      );

      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      // Check for server-side errors in success response
      if (response.data.success === false) {
        throw new Error(response.data.error || 'Analysis failed on server');
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with user-friendly messages
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        const statusMessages = {
          400: errorData?.error || 'Invalid YouTube URL or video not accessible.',
          401: 'Authentication failed. Please check API configuration.',
          403: 'Access denied. Check API permissions.',
          404: 'Video not found or transcript not available.',
          408: 'Request timeout. Try with a shorter video or check connection.',
          429: 'Rate limit exceeded. Please wait before trying again.',
          500: 'Server error. Please try again in a moment.',
          502: 'Backend service unavailable. Please try again.',
          503: 'Service temporarily down for maintenance.',
          504: 'Gateway timeout. The request took too long.',
        };
        
        const message = statusMessages[status] || 
          `Service error (${status}): ${errorData?.error || 'Unknown error'}`;
        throw new Error(message);
        
      } else if (error.request) {
        // Network/connection errors
        if (error.name === 'AbortError') {
          throw new Error('Request was cancelled.');
        }
        
        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timeout. Try again or use a shorter video.');
        }
        
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
          throw new Error('Cannot connect to analysis service. Please check your internet connection.');
        }
        
        if (error.code === 'ERR_INVALID_URL') {
          throw new Error('Invalid server configuration. Please contact support.');
        }
        
        throw new Error(`Network error: ${error.message || 'Connection failed'}`);
      }
      
      // Handle validation and other errors
      throw new Error(error.message || 'An unexpected error occurred. Please try again.');
    }
  },

  // Enhanced connection test with diagnostics
  async testConnection() {
    const startTime = Date.now();
    
    try {
      const response = await axios.get(`${API}/health`, { timeout: 10000 });
      const duration = Date.now() - startTime;
      
      return {
        success: true,
        message: 'Backend connection successful',
        data: response.data,
        diagnostics: {
          backendUrl: BACKEND_URL,
          apiEndpoint: API,
          responseTime: `${duration}ms`,
          timestamp: new Date().toISOString(),
          protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
        }
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
        diagnostics: {
          backendUrl: BACKEND_URL,
          apiEndpoint: API,
          errorCode: error.code,
          errorType: error.name,
          responseTime: `${Date.now() - startTime}ms`,
          timestamp: new Date().toISOString(),
          protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
        }
      };
    }
  },

  // Search YouTube
  async searchYouTube(query, maxResults = 10) {
    try {
      if (!query || query.trim().length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const response = await axios.get(
        `${API}/search-youtube`,
        {
          params: {
            query: query.trim(),
            max_results: maxResults
          },
          timeout: 30000
        }
      );

      if (!response.data || response.data.success === false) {
        throw new Error(response.data?.error || 'Failed to search YouTube');
      }

      return response.data;
    } catch (error) {
      console.error('YouTube search error:', error);
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        throw new Error(errorData?.detail || errorData?.error || `Failed to search YouTube (${status})`);
      }
      throw error;
    }
  },

  // Get playlist statistics
  async getPlaylistStats(playlistUrl) {
    try {
      if (!playlistUrl || typeof playlistUrl !== 'string') {
        throw new Error('Please provide a valid playlist URL');
      }

      const response = await axios.post(
        `${API}/playlist-stats`,
        {
          playlist_url: playlistUrl
        },
        { timeout: 120000 } // 2 minutes for playlist stats
      );

      if (!response.data || response.data.success === false) {
        throw new Error(response.data?.error || 'Failed to fetch playlist stats');
      }

      return response.data;
    } catch (error) {
      console.error('Playlist stats error:', error);
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        throw new Error(errorData?.detail || errorData?.error || `Failed to fetch playlist stats (${status})`);
      }
      throw error;
    }
  },

  // Compare playlists
  async comparePlaylists(playlistUrls, analysisLanguage = 'hinglish') {
    try {
      if (!Array.isArray(playlistUrls) || playlistUrls.length !== 2) {
        throw new Error('Please provide exactly 2 playlist URLs');
      }

      const response = await axios.post(
        `${API}/compare-playlists`,
        {
          playlist_urls: playlistUrls,
          analysis_language: analysisLanguage
        },
        { timeout: 120000 } // 2 minutes for playlist comparison
      );

      if (!response.data || response.data.success === false) {
        throw new Error(response.data?.error || 'Failed to compare playlists');
      }

      return response.data;
    } catch (error) {
      console.error('Playlist comparison error:', error);
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        throw new Error(errorData?.detail || errorData?.error || `Failed to compare playlists (${status})`);
      }
      throw error;
    }
  },

  // Extract videos from a YouTube playlist
  async extractPlaylistVideos(playlistUrl) {
    try {
      if (!playlistUrl || typeof playlistUrl !== 'string') {
        throw new Error('Please provide a valid playlist URL');
      }

      // Validate playlist URL
      const playlistPattern = /youtube\.com\/.*[?&]list=([a-zA-Z0-9_-]+)/;
      if (!playlistPattern.test(playlistUrl)) {
        throw new Error('Please enter a valid YouTube playlist URL');
      }

      const response = await axios.get(
        `${API}/extract-playlist-videos`,
        { 
          params: { playlist_url: playlistUrl },
          timeout: 30000
        }
      );

      if (!response.data || response.data.success === false) {
        throw new Error(response.data?.error || 'Failed to extract playlist videos');
      }

      return response.data;
    } catch (error) {
      console.error('Playlist extraction error:', error);
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        throw new Error(errorData?.detail || errorData?.error || `Failed to extract playlist (${status})`);
      }
      throw error;
    }
  },

  // Analyze YouTube channel
  async analyzeChannel(channelUrl) {
    try {
      const response = await axios.post(
        `${API}/analyze-channel`,
        { channel_url: channelUrl },
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('Channel analysis error:', error);
      throw error;
    }
  },

  // Compare channels
  async compareChannels(channelUrls) {
    try {
      const response = await axios.post(
        `${API}/compare-channels`,
        { channel_urls: channelUrls },
        { timeout: 60000 }
      );
      return response.data;
    } catch (error) {
      console.error('Channel comparison error:', error);
      throw error;
    }
  },

  // Get category rankings
  async getCategoryRankings(category = null, limit = 20) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      params.append('limit', limit);
      const response = await axios.get(
        `${API}/category-rankings?${params.toString()}`,
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('Category rankings error:', error);
      throw error;
    }
  },

  async askTranscriptQuestion(videoId, question, analysisLanguage = 'hinglish', history = []) {
    try {
      const response = await axios.post(
        `${API}/chat-with-video`,
        {
          video_id: videoId,
          question,
          analysis_language: analysisLanguage,
          history,
        },
        { timeout: 45000 }
      );

      return response.data;
    } catch (error) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error(error.message || 'Failed to ask about the video.');
    }
  },

  // Export CSV
  async exportCSV(analysisData) {
    try {
      const response = await axios.post(
        `${API}/export-csv`,
        { analysis_data: analysisData },
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('CSV export error:', error);
      throw error;
    }
  },

  // Export PDF
  async exportPDF(analysisData) {
    try {
      const response = await axios.post(
        `${API}/export-pdf`,
        { analysis_data: analysisData },
        { timeout: 60000, responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('PDF export error:', error);
      throw error;
    }
  },

  // Get Social Blade data
  async getSocialBladeData(channelId, channelUrl = null) {
    try {
      const params = new URLSearchParams();
      if (channelId) params.append('channel_id', channelId);
      if (channelUrl) params.append('channel_url', channelUrl);
      const response = await axios.get(
        `${API}/social-blade-data?${params.toString()}`,
        { timeout: 30000 }
      );
      return response.data;
    } catch (error) {
      console.error('Social Blade data error:', error);
      throw error;
    }
  },

  // Utility method to get current configuration
  getConfig() {
    return {
      backendUrl: BACKEND_URL,
      apiEndpoint: API,
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
      protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
    };
  },

  // Track user activity
  async trackActivity(activityData) {
    try {
      const response = await axios.post(`${API}/track-activity`, activityData, {
        timeout: 10000
      });
      return response.data;
    } catch (error) {
      console.error('Activity tracking error:', error);
      // Don't throw - activity tracking should not break the main flow
      return { success: false, error: error.message };
    }
  },

  // Admin APIs
  async getAdminActivity(userEmail) {
    try {
      const response = await axios.get(`${API}/admin/activity`, {
        params: { user_email: userEmail },
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Admin activity fetch error:', error);
      throw error;
    }
  },

  async getAdminStats(userEmail) {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        params: { user_email: userEmail },
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Admin stats fetch error:', error);
      throw error;
    }
  },

  async getAdminUsers(userEmail) {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { user_email: userEmail },
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Admin users fetch error:', error);
      throw error;
    }
  },

  async getAdminVideos(userEmail) {
    try {
      const response = await axios.get(`${API}/admin/videos`, {
        params: { user_email: userEmail },
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Admin videos fetch error:', error);
      throw error;
    }
  },

  // Re-SEO Analysis
  async getReSEO(youtubeUrl) {
    try {
      const response = await axios.post(`${API}/re-seo`, {
        youtube_url: youtubeUrl
      }, {
        timeout: 300000 // 5 minutes for comprehensive SEO analysis
      });
      return response.data;
    } catch (error) {
      console.error('Re-SEO analysis error:', error);
      throw error;
    }
  },

  // Generate Mind Map
  async generateMindMap(youtubeUrl) {
    try {
      const response = await axios.post(`${API}/generate-mindmap`, {
        youtube_url: youtubeUrl
      }, {
        timeout: 300000 // 5 minutes for mind map generation
      });
      return response.data;
    } catch (error) {
      console.error('Mind map generation error:', error);
      throw error;
    }
  },
};

// Leaderboard API
export const getLeaderboard = async (limit = 20, language = null) => {
  try {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (language) {
      params.append('language', language);
    }

    const response = await axios.get(`${API}/leaderboard?${params.toString()}`, {
      timeout: 60000  // Increased to 60 seconds for initial metadata fetch
    });

    if (!response.data) {
      throw new Error('Invalid response from server');
    }

    if (response.data.success === false) {
      throw new Error(response.data.error || 'Failed to fetch leaderboard');
    }

    return response.data;
  } catch (error) {
    console.error('Leaderboard fetch error:', error);
    throw error;
  }
};

export default apiService;
